"use client";

import { useCallback, useReducer } from "react";
import { BreakoutData, CreateEventFormData, GeneralSessionData, WizardAction } from "../_types/wizard-types";
import { EMPTY_MEETING_SPACE_SCHEDULE } from "../_logic/constants";
import { toggleLocation } from "../_logic/location-utils";

const EMPTY_GENERAL_SESSION: GeneralSessionData = {
  needed: null,
  roomCount: 0,
  capacity: null,
  roomTypes: [],
  notes: "",
  schedule: EMPTY_MEETING_SPACE_SCHEDULE,
};

const EMPTY_BREAKOUT: BreakoutData = {
  needed: null,
  count: 0,
  capacityMin: null,
  capacityMax: null,
  roomTypes: [],
  notes: "",
  schedule: EMPTY_MEETING_SPACE_SCHEDULE,
};

/** Initial form state with all fields empty/default. */
export const INITIAL_FORM_DATA: CreateEventFormData = {
  name: "",
  formattedLocations: [],
  nearPlace: null,
  numPeople: 0,
  accommodations: {
    needed: false,
    checkIn: null,
    checkOut: null,
    maxRoomsPerNight: 0,
  },
  accommodationsDetailed: null,
  meetingRoomsDetailedLegacy: null,
  meetingRooms: {
    needed: null,
    dates: { start: null, end: null },
    numRooms: 0,
    generalSession: EMPTY_GENERAL_SESSION,
    breakout: EMPTY_BREAKOUT,
  },
  foodBeverage: { needed: null, meals: {}, details: "" },
  eventDates: null,
  eventDatesAlternates: [],
  eventDatesNotes: "",
  flexibility: null,
  budgetVenue: null,
  budgetUnknown: false,
  budgetFlights: null,
  budgetFoodBeverage: null,
  budgetAv: null,
  budgetTransportation: null,
  budgetActivities: null,
  budgetMerch: null,
  budgetOther: null,
  budgetRoomRate: null,
  budgetCurrency: null,
  notesLocationBudget: "",
  decisionDeadline: null,
  decisionFactors: [],
  decisionFactorOtherText: null,
  additionalServices: [],
  agendaItems: [],
  customQuestions: [],
  customConcessions: [],
  eventSource: null,
  eventSourceOtherText: null,
  additionalServicesOtherText: "",
  additionalNotes: "",
  documentAttachments: [],
  draftEventId: null,
};

/**
 * Merges planning-doc extractions into the form without overwriting anything the user has typed.
 * A field is filled only while it still equals its `INITIAL_FORM_DATA` value, or the value a
 * last-event prefill put there, so a late parse can never clobber an answer. Pure, so the step can
 * preview which fields a dispatch will fill.
 *
 * @param state - Current form data.
 * @param prefill - Extracted values, keyed by form field.
 * @param overridable - Values carried over from the user's last event; a field still holding one is
 *   treated as unanswered.
 * @returns The merged form data and the fields that were actually filled.
 */
export function applyPrefill(
  state: CreateEventFormData,
  prefill: Partial<CreateEventFormData>,
  overridable: Partial<CreateEventFormData> = {}
): { next: CreateEventFormData; filled: (keyof CreateEventFormData)[] } {
  const isUnanswered = (key: keyof CreateEventFormData) =>
    JSON.stringify(state[key]) === JSON.stringify(INITIAL_FORM_DATA[key]) ||
    (key in overridable && JSON.stringify(state[key]) === JSON.stringify(overridable[key]));
  const filled = (Object.keys(prefill) as (keyof CreateEventFormData)[]).filter(
    (key) => prefill[key] !== undefined && isUnanswered(key)
  );
  const next = { ...state, ...Object.fromEntries(filled.map((key) => [key, prefill[key]])) };
  return { next, filled };
}

/** Derives the legacy flat `meetingRooms.numRooms` from the general-session/breakout breakdown,
 *  so `events.num_meeting_rooms_needed` keeps working without the planner filling in a separate
 *  flat count. */
const deriveNumRooms = (
  generalSession: { needed: boolean | null; roomCount: number },
  breakout: { needed: boolean | null; count: number }
): number =>
  (generalSession.needed ? Math.max(0, generalSession.roomCount) : 0) +
  (breakout.needed ? Math.max(0, breakout.count) : 0);

/**
 * Reducer for the wizard form data with cascade-clear logic.
 *
 * @param state - Current accumulated wizard form data.
 * @param action - The dispatched action describing the field change.
 * @returns The next form-data state (unchanged reference for unknown actions).
 */
export function wizardReducer(state: CreateEventFormData, action: WizardAction): CreateEventFormData {
  switch (action.type) {
    case "SET_NAME":
      return { ...state, name: action.payload };

    case "SET_NUM_PEOPLE":
      return { ...state, numPeople: action.payload };

    case "SET_LOCATION":
      return { ...state, formattedLocations: action.payload.formattedLocations };

    case "SET_NEAR_PLACE":
      return { ...state, nearPlace: action.payload };

    case "TOGGLE_LOCATION": {
      // Operates on current reducer state so rapid (concurrent) pill toggles compose atomically
      // instead of racing on a stale snapshot.
      const formattedLocations = toggleLocation(
        state.formattedLocations,
        action.payload.location,
        action.payload.limit
      );
      return { ...state, formattedLocations };
    }

    case "RESOLVE_LOCATION": {
      // Reconcile an optimistically-added placeholder: swap it for the resolved location, or drop it on
      // failure. No-op when the placeholder is already gone (the user removed it mid-resolve).
      const idx = state.formattedLocations.findIndex((loc) => loc.place_id === action.payload.placeId);
      if (idx === -1) return state;
      const formattedLocations = [...state.formattedLocations];
      if (action.payload.location) formattedLocations[idx] = action.payload.location;
      else formattedLocations.splice(idx, 1);
      return { ...state, formattedLocations };
    }

    case "PREFILL_LOCATIONS":
      // Non-destructive: only fills when the user has no location yet, so an async prefill (Northstar)
      // can never clobber a choice the user already made.
      if (state.formattedLocations.length > 0) return state;
      return { ...state, formattedLocations: action.payload.formattedLocations };

    case "SET_ACCOMMODATIONS_NEEDED":
      return {
        ...state,
        accommodations: action.payload
          ? { ...state.accommodations, needed: true }
          : { needed: false, checkIn: null, checkOut: null, maxRoomsPerNight: 0 },
        accommodationsDetailed: action.payload ? state.accommodationsDetailed : null,
      };

    case "SET_ACCOMMODATION_DATES":
      // Same range (e.g. clicking Yes on the gate again) keeps the entered breakdown.
      if (
        action.payload.checkIn === state.accommodations.checkIn &&
        action.payload.checkOut === state.accommodations.checkOut
      ) {
        return state;
      }
      return {
        ...state,
        accommodations: {
          ...state.accommodations,
          checkIn: action.payload.checkIn,
          checkOut: action.payload.checkOut,
          maxRoomsPerNight: 0,
        },
        accommodationsDetailed: null,
      };

    case "SET_MAX_ROOMS_PER_NIGHT":
      return {
        ...state,
        accommodations: { ...state.accommodations, maxRoomsPerNight: action.payload },
      };

    case "SET_ACCOMMODATIONS_DETAILED":
      return { ...state, accommodationsDetailed: action.payload };

    case "SET_MEETING_ROOMS_NEEDED":
      // Cascade-clear dates and the room breakdown when meeting rooms aren't needed. Food &
      // beverage is its own step now, so it's untouched here regardless of this answer.
      return {
        ...state,
        meetingRooms: action.payload
          ? { ...state.meetingRooms, needed: true }
          : {
              needed: false,
              dates: { start: null, end: null },
              numRooms: 0,
              generalSession: EMPTY_GENERAL_SESSION,
              breakout: EMPTY_BREAKOUT,
            },
      };

    case "SET_MEETING_DATES":
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          dates: { start: action.payload.start, end: action.payload.end },
        },
      };

    case "SET_GENERAL_SESSION_NEEDED": {
      const generalSession = action.payload
        ? { ...state.meetingRooms.generalSession, needed: true }
        : { ...EMPTY_GENERAL_SESSION, needed: false };
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          generalSession,
          numRooms: deriveNumRooms(generalSession, state.meetingRooms.breakout),
        },
      };
    }

    case "SET_GENERAL_SESSION_ROOM_COUNT": {
      const generalSession = { ...state.meetingRooms.generalSession, roomCount: action.payload };
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          generalSession,
          numRooms: deriveNumRooms(generalSession, state.meetingRooms.breakout),
        },
      };
    }

    case "SET_GENERAL_SESSION_CAPACITY":
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          generalSession: { ...state.meetingRooms.generalSession, capacity: action.payload },
        },
      };

    case "SET_GENERAL_SESSION_ROOM_TYPES":
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          generalSession: { ...state.meetingRooms.generalSession, roomTypes: action.payload },
        },
      };

    case "SET_GENERAL_SESSION_NOTES":
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          generalSession: { ...state.meetingRooms.generalSession, notes: action.payload },
        },
      };

    case "SET_BREAKOUT_NEEDED": {
      const breakout = action.payload
        ? { ...state.meetingRooms.breakout, needed: true }
        : { ...EMPTY_BREAKOUT, needed: false };
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          breakout,
          numRooms: deriveNumRooms(state.meetingRooms.generalSession, breakout),
        },
      };
    }

    case "SET_BREAKOUT_COUNT": {
      const breakout = { ...state.meetingRooms.breakout, count: action.payload };
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          breakout,
          numRooms: deriveNumRooms(state.meetingRooms.generalSession, breakout),
        },
      };
    }

    case "SET_BREAKOUT_CAPACITY_RANGE":
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          breakout: {
            ...state.meetingRooms.breakout,
            capacityMin: action.payload.min,
            capacityMax: action.payload.max,
          },
        },
      };

    case "SET_BREAKOUT_ROOM_TYPES":
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          breakout: { ...state.meetingRooms.breakout, roomTypes: action.payload },
        },
      };

    case "SET_BREAKOUT_NOTES":
      return {
        ...state,
        meetingRooms: {
          ...state.meetingRooms,
          breakout: { ...state.meetingRooms.breakout, notes: action.payload },
        },
      };

    case "SET_MEETING_SPACE_SCHEDULE": {
      const { space, schedule } = action.payload;
      return {
        ...state,
        meetingRooms: { ...state.meetingRooms, [space]: { ...state.meetingRooms[space], schedule } },
      };
    }

    case "SET_FOOD_BEVERAGE_NEEDED":
      // Clearing the selections and detail text on "No" keeps them from silently resurfacing (and
      // getting submitted) if the user re-answers "Yes" later.
      return {
        ...state,
        foodBeverage: action.payload
          ? { ...state.foodBeverage, needed: true }
          : { needed: false, meals: {}, details: "" },
      };

    case "SET_EVENT_DATES":
      return { ...state, eventDates: action.payload };

    case "SET_EVENT_DATES_ALTERNATES":
      return { ...state, eventDatesAlternates: action.payload };

    case "SET_EVENT_DATES_NOTES":
      return { ...state, eventDatesNotes: action.payload };

    case "SET_FLEXIBILITY":
      return { ...state, flexibility: action.payload };

    case "SET_BUDGET_VENUE":
      return { ...state, budgetVenue: action.payload };

    case "SET_BUDGET_UNKNOWN":
      return { ...state, budgetUnknown: action.payload, budgetVenue: action.payload ? null : state.budgetVenue };

    case "SET_BUDGET_CURRENCY":
      return { ...state, budgetCurrency: action.payload };

    case "SET_BUDGET_BREAKDOWN":
      return { ...state, ...action.payload };

    case "SET_NOTES_LOCATION_BUDGET":
      return { ...state, notesLocationBudget: action.payload };

    case "SET_DECISION_DEADLINE":
      return { ...state, decisionDeadline: action.payload };

    case "SET_DECISION_FACTORS":
      return { ...state, decisionFactors: action.payload };

    case "SET_DECISION_FACTOR_OTHER_TEXT":
      return { ...state, decisionFactorOtherText: action.payload };

    case "SET_ADDITIONAL_SERVICES":
      return { ...state, additionalServices: action.payload };

    case "SET_ADDITIONAL_SERVICES_OTHER_TEXT":
      return { ...state, additionalServicesOtherText: action.payload };

    case "SET_AGENDA_ITEMS":
      return { ...state, agendaItems: action.payload };

    case "SET_FOOD_BEVERAGE_DETAILS":
      return { ...state, foodBeverage: { ...state.foodBeverage, details: action.payload } };

    case "SET_FOOD_BEVERAGE_MEALS":
      return { ...state, foodBeverage: { ...state.foodBeverage, meals: action.payload } };

    case "SET_CUSTOM_QUESTIONS":
      return { ...state, customQuestions: action.payload };

    case "SET_CUSTOM_CONCESSIONS":
      return { ...state, customConcessions: action.payload };

    case "SET_EVENT_SOURCE":
      return { ...state, eventSource: action.payload };

    case "SET_EVENT_SOURCE_OTHER_TEXT":
      return { ...state, eventSourceOtherText: action.payload };

    case "SET_ADDITIONAL_NOTES":
      return { ...state, additionalNotes: action.payload };

    case "ADD_DOCUMENT_ATTACHMENT":
      return { ...state, documentAttachments: [...state.documentAttachments, action.payload] };

    case "REMOVE_DOCUMENT_ATTACHMENT":
      return {
        ...state,
        documentAttachments: state.documentAttachments.filter((a) => a.storagePath !== action.payload.storagePath),
      };

    case "SET_DRAFT_EVENT_ID":
      return { ...state, draftEventId: action.payload };

    case "HYDRATE_DRAFT":
      return { ...state, ...action.payload };

    case "PREFILL_FROM_DOCUMENT":
      return applyPrefill(state, action.payload, action.overridable).next;

    case "SET_NORTHSTAR_CONTEXT":
      return {
        ...state,
        northstarRequestId: action.payload.requestId,
        northstarVenueIds: action.payload.venueIds,
      };

    default:
      return state;
  }
}

/**
 * Manages all wizard form data via useReducer, with console logging on every dispatch.
 *
 * @param initial - Optional starting form data. Falls back to INITIAL_FORM_DATA.
 *   Used when resuming the wizard from sessionStorage after an auth round-trip.
 * @returns Tuple of [formData, loggingDispatch].
 */
export function useWizardState(
  initial?: CreateEventFormData
): readonly [CreateEventFormData, (action: WizardAction) => void] {
  const [formData, rawDispatch] = useReducer(wizardReducer, initial ?? INITIAL_FORM_DATA);

  const dispatch = useCallback(
    (action: WizardAction) => {
      rawDispatch(action);
      if (process.env.NODE_ENV === "development") {
        console.log(`[wizard] ${action.type}`, action.payload);
      }
    },
    [rawDispatch]
  );

  return [formData, dispatch] as const;
}
